import React from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { blogPosts } from '../blogData'; // Import data
import SEO from '../components/SEO'; // Assuming you have an SEO component
import SchemaMarkup from '../components/SchemaMarkup'; // Assuming you have this

// Placeholder blogPosts data - replace with actual import // This comment is now obsolete
// const blogPosts = [ ... ]; // Removed

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);

  if (!post) {
    return (
      <div className="pt-24 pb-16 text-center text-lightText">
        <h1 className="text-4xl font-bold">Post not found</h1>
        <p className="mt-4">Sorry, we couldn\'t find the blog post you\'re looking for.</p>
        <a href="/blog" className="mt-8 inline-block bg-primary text-darkBg font-bold py-3 px-6 rounded-md hover:bg-primary/90 transition-colors">
          Back to Blog
        </a>
      </div>
    );
  }

  // Basic Markdown to HTML conversion (replace with a library like react-markdown for robust parsing)
  const renderMarkdown = (markdown) => {
    // Replace ## headings
    let html = markdown.replace(/^## (.*$)/gim, '<h2 class="text-3xl font-bold text-lightText mt-8 mb-4">$1</h2>');
    // Replace #### headings
    html = html.replace(/^#### (.*$)/gim, '<h4 class="text-xl font-semibold text-lightText mt-6 mb-3">$1</h4>');
    // Replace paragraphs (simple version: consecutive newlines)
    html = html.split('\n\n').map(paragraph => `<p class="text-lightText/90 mb-4 leading-relaxed">${paragraph.replace(/\n/g, '<br />')}</p>`).join('');
    // Replace bold text **text**
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    // Replace italic text *text* (ensure it doesn't conflict with list items if you add them)
    html = html.replace(/\*(?!\* )(.*?)\*/g, '<em>$1</em>');
     // Replace placeholder image text with an actual image tag (example)
    html = html.replace(/Josh Kolonia\'s FPV drone after a paintball flight at Skirmish/g, '<img src="/images/20241019_164159.jpg" alt="FPV Drone after paintball" class="my-6 rounded-lg shadow-lg" />'); // You'll need to add this image

    return { __html: html };
  };

  return (
    <div className="blog-post-page pt-32 pb-16 bg-darkBg">
      <SEO 
        title={`${post.title} | Cosmik Drones Blog`}
        description={post.excerpt}
        canonicalUrl={`https://cosmikdrones.com/blog/${post.slug}`}
        image={`https://cosmikdrones.com${post.image}`}
      />
      <SchemaMarkup 
        type="BlogPosting"
        data={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `https://cosmikdrones.com/blog/${post.slug}`
          },
          "headline": post.title,
          "description": post.excerpt,
          "image": `https://cosmikdrones.com${post.image}`,
          "author": {
            "@type": "Person", // Or Organization if preferred
            "name": post.authorName,
            // "url": "https://cosmikdrones.com/about" // Optional: Link to author page
          },
          "publisher": {
            "@type": "Organization",
            "name": "Cosmik Drones",
            "logo": {
              "@type": "ImageObject",
              "url": "https://cosmikdrones.com/Cosmik%20Logo/Kolonia_Logo.png"
            }
          },
          "datePublished": post.date, // Ensure post.date is in ISO 8601 format (YYYY-MM-DD)
          "dateModified": post.date // Or a separate modified date if available
          // "articleBody": post.fullContent // Can be included, but might be very long.
        }}
      />
      <div className="section-container max-w-3xl mx-auto">
        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <header className="mb-8">
            <span className="text-primary font-medium mb-2 block">{post.category}</span>
            <h1 className="text-4xl md:text-5xl font-bold text-lightText mb-4 leading-tight">{post.title}</h1>
            <div className="flex items-center text-lightText/70">
              <img 
                src={post.authorImage} 
                alt={post.authorName} 
                className="w-10 h-10 rounded-full object-cover mr-3"
              />
              <div>
                <span className="font-medium text-lightText/90">{post.authorName}</span> / <span>{post.date}</span>
              </div>
            </div>
          </header>

          {post.image && (
            <div className="mb-8 rounded-lg overflow-hidden shadow-xl aspect-video">
              <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
            </div>
          )}
          
          {/* Rendered Markdown Content */}
          <div className="prose prose-invert max-w-none text-lightText/90" dangerouslySetInnerHTML={renderMarkdown(post.fullContent)} />

          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="mt-12 pt-8 border-t border-darkCard/50 text-center"
          >
            <a 
              href="/blog" 
              className="bg-primary hover:bg-primary/90 text-darkBg font-bold py-3 px-8 rounded-md transition-colors inline-block"
            >
              ← Back to All Posts
            </a>
          </motion.div>
        </motion.article>
      </div>
    </div>
  );
};

export default BlogPostPage; 